import { FC, ReactNode } from 'react';

import { Id, toast, ToastOptions, TypeOptions } from 'react-toastify';

import {
  DefaultToast,
  ErrorToast,
  InfoToast,
  SuccessToast,
  ToastProps,
  WarningToast,
} from '@hsp/react-component-library';

import { INotification } from '@c/notifications/types';

import { s } from 'utilities/strings';

const show = <TData extends INotification>(
  message: ReactNode,
  type: TypeOptions,
  title?: ReactNode,
  options?: ToastOptions<TData>
): Id => {
  let Component: FC<ToastProps>;

  title = title ?? s(['Site.HSP', 'Site.Studio']);

  switch (type) {
    case 'error':
      Component = ErrorToast;
      break;

    case 'info':
      Component = InfoToast;
      break;

    case 'success':
      Component = SuccessToast;
      break;

    case 'warning':
      Component = WarningToast;
      break;

    default:
      Component = DefaultToast;
      break;
  }

  return toast(<Component title={title}>{message}</Component>, options);
};

export const notifications = {
  default: <TData extends INotification>(message: ReactNode, title?: ReactNode, options?: ToastOptions<TData>) =>
    show(message, 'default', title, {
      ...options,
      data: { ...options?.data, content: message },
    }),
  error: <TData extends INotification>(message: ReactNode, title?: ReactNode, options?: ToastOptions<TData>) =>
    show(message, 'error', title, {
      ...options,
      data: { ...options?.data, content: message },
      progressClassName: 'error',
    }),
  info: <TData extends INotification>(message: ReactNode, title?: ReactNode, options?: ToastOptions<TData>) =>
    show(message, 'info', title, {
      ...options,
      data: { ...options?.data, content: message },
      progressClassName: 'info',
    }),
  success: <TData extends INotification>(message: ReactNode, title?: ReactNode, options?: ToastOptions<TData>) =>
    show(message, 'success', title, {
      ...options,
      data: { ...options?.data, content: message },
      progressClassName: 'success',
    }),
  warning: <TData extends INotification>(message: ReactNode, title?: ReactNode, options?: ToastOptions<TData>) =>
    show(message, 'warning', title, {
      ...options,
      data: { ...options?.data, content: message },
      progressClassName: 'warning',
    }),
};
